import React, { useEffect, useState, useRef } from "react";
import PatientDetails from "./PatientDetails";
import { TabContext, TabPanel } from "@mui/lab";
import { Tabs, Tab, Grid2, IconButton, Box } from "@mui/material";
import PatientList from "./PatientList";
import CloseIcon from "@mui/icons-material/Close";
import NewPatient from "./NewPatient";
import EditPatient from "./EditPatient";
import PrescriptionReadOnlyView from "../Prescriptions/PrescriptionReadOnlyView";
import { useDebouncedEffect } from "../Utility/Utility"; // ✅ NEW IMPORT
import { clearFormDataFromLocal, getFormDataFromLocal } from "../Utility/FormDataTempStorage";
import UnsavedDataConfirmationDialog from "../common/UnsavedDataConfirmationDialog";
import PatientDashboard from "./PatientDashboard";
function PatientHome() {
  const patientListRef = useRef();

  // State Initialization
  const [activeTab, setActiveTab] = useState(() => {
    const savedActiveTab = localStorage.getItem("activeTab");
    return savedActiveTab || "Dashboard"; 
  });


const [tabs, setTabs] = React.useState(() => {
  const savedTabs = localStorage.getItem("patients-tabs");
  const defaultTabs = [
    { label: "Dashboard", value: "Dashboard" },
    { label: "Patients", value: "Patients" },
  ];
  if (!savedTabs) return defaultTabs;

  const parsed = JSON.parse(savedTabs);
  const dynamic = parsed.filter(
    (t) => t.value !== "Dashboard" && t.value !== "Patients"
  );
  return [
    { label: "Dashboard", value: "Dashboard" },
    { label: "Patients", value: "Patients" },
    ...dynamic,
  ];
});

  // ✅ NEW: Tab history for smart close (last clicked tab becomes active on close)
  const [tabHistory, setTabHistory] = useState(() => {
    const saved = localStorage.getItem("PatientTabHistory");
    return saved ? JSON.parse(saved) : ["Patients"];
  });
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
const [tabToClose, setTabToClose] = useState(null);
  const [newPatientTabNumber, setNewPatientTabNumber] = useState(0);

  // ✅ NEW: Track tab history whenever active tab changes
  useEffect(() => {
    setTabHistory((prev) => {
      if (prev[prev.length - 1] === activeTab) return prev;
      const filtered = prev.filter((t) => t !== activeTab);
      return [...filtered, activeTab];
    });
  }, [activeTab]);

  // Effect Hooks (unchanged)
  useEffect(() => {
    localStorage.setItem("patients-tabs", JSON.stringify(tabs));
  }, [tabs]);

  useEffect(() => {
    localStorage.setItem("activeTab", activeTab);
  }, [activeTab]);

  // ✅ NEW: Persist tab history with debounce
  useDebouncedEffect(
    () => {
      localStorage.setItem("PatientTabHistory", JSON.stringify(tabHistory));
    },
    [tabHistory],
    500,
  );

  const openPrescriptionViewTab = (
    prescriptionId,
    prescriptionDisplayId,
    prescriptionType
  ) => {
    setTabs((prevTabs) => {
      const existingTab = tabs.find((tab) => tab.value === prescriptionId);
      if (existingTab) {
        setActiveTab(existingTab.value);
      } else {
        const newTab = {
          label: prescriptionDisplayId || "Prescription",
          value: prescriptionId,
          type: "ViewPrescription",
          prescriptionId: prescriptionId,
          prescriptionType: prescriptionType,
        };

        setActiveTab(prescriptionId);
        return [...prevTabs, newTab];
      }
      return prevTabs;
    });
  };

  const handleEditPatientTab = (patient) => {
    setTabs((prevTabs) => {
      const existingTab = prevTabs.find(
        (tab) => tab.value === `EditPatient-${patient.id}`
      );

      if (existingTab) {
        setActiveTab(existingTab.value);
        return prevTabs;
      }

      const newTab = {
        label: `Edit ${patient.firstName} ${patient.lastName}`,
        value: `EditPatient-${patient.id}`,
        type: "EditPatient",
        patient: patient,
      };

      setActiveTab(newTab.value);
      return [...prevTabs, newTab];
    });
  };

  const handleSaveEdit = (updatedPatient) => {
    const updatedTabs = tabs.filter(
      (tab) => !tab.value.startsWith("EditPatient")
    );
    setTabs(updatedTabs);

    const patientDetailsTab = tabs.find(
      (tab) => tab.value === `patient-${updatedPatient.id}`
    );
    if (patientDetailsTab) {
      setActiveTab(patientDetailsTab.value);
    } else {
      setActiveTab("Patients");
    }
  };

  const onNewPatientAdded = (tempNewPatientId) => {
    setTabs((prevTabs) =>
      prevTabs.filter((tab) => tab.value !== tempNewPatientId)
    );
    setActiveTab("Patients");
    patientListRef?.current?.refreshPatientList();
  };

  const handleViewDetails = (row) => {
    const existingTab = tabs.find((tab) => tab.value === row?.id);

    if (existingTab) {
      setActiveTab(existingTab.value);
      return;
    }

    const newTab = {
      label: `${row.firstName} ${row.lastName}`,
      value: row?.id,
      type: "PatientDetails",
      patientId: row?.id,
    };

    setTabs((prevTabs) => [...prevTabs, newTab]);
    setActiveTab(newTab.value);
  };

  // ✅ CHANGED: Now uses tabHistory for smart tab switching on close
  const handleCloseTab = (tabValue) => {
  if (tabValue === "Patients" || tabValue === "Dashboard") return;

  const tab = tabs.find((t) => t.value === tabValue);
  let storageKey = tabValue;

  if (tab) {
    if (tab.type === "NewPatient") {
      storageKey = `NewPatient-${tab.tempNewPatientId}`;
    } else if (tab.type === "EditPatient") {
      storageKey = `EditPatient-${tab.patient?.id}`;
    }
  }

  const unsavedData = getFormDataFromLocal(storageKey);
  if (unsavedData) {
    setShowUnsavedChangesDialog(true);
    setTabToClose(storageKey);
    return;
  }

  setTabs((prevTabs) => prevTabs.filter((tab) => tab.value !== tabValue));
  setTabHistory((prev) => {
    const filtered = prev.filter((t) => t !== tabValue);
    if (activeTab === tabValue) {
      const remainingTabs = tabs
        .filter((tab) => tab.value !== tabValue)
        .map((tab) => tab.value);
      const lastActive = [...filtered].reverse().find((t) => remainingTabs.includes(t));
      setActiveTab(lastActive || "Patients");
    }
    localStorage.setItem("PatientTabHistory", JSON.stringify(filtered));
    return filtered;
  });
};

const handleDiscardChanges = () => {
  const tabValueToClose = tabs.find((t) => {
    let storageKey = t.value;
    if (t.type === "NewPatient") storageKey = `NewPatient-${t.tempNewPatientId}`;
    else if (t.type === "EditPatient") storageKey = `EditPatient-${t.patient?.id}`;
    return storageKey === tabToClose;
  })?.value || activeTab;

  clearFormDataFromLocal(tabToClose);
  
  setTabs((prevTabs) => prevTabs.filter((tab) => tab.value !== tabValueToClose));
  setTabHistory((prev) => {
    const filtered = prev.filter((t) => t !== tabValueToClose);
    if (activeTab === tabValueToClose) {
      const remainingTabs = tabs
        .filter((tab) => tab.value !== tabValueToClose)
        .map((tab) => tab.value);
      const lastActive = [...filtered].reverse().find((t) => remainingTabs.includes(t));
      setActiveTab(lastActive || "Patients");
    }
    localStorage.setItem("PatientTabHistory", JSON.stringify(filtered));
    return filtered;
  });

  setShowUnsavedChangesDialog(false);
  setTabToClose(null);
};

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleNewPatientTab = () => {
    const newTabNumber = newPatientTabNumber + 1;
    setNewPatientTabNumber(newTabNumber);

    const newTab = {
      label: `New Patient (${newTabNumber})`,
      value: `NewPatient-${newTabNumber}`,
      type: "NewPatient",
      tempNewPatientId: `NewPatient-${newTabNumber}`,
    };

    setTabs((prevTabs) => [...prevTabs, newTab]);
    setActiveTab(newTab.value);
  };

  return (
    <>
    <Grid2 container>
      <TabContext value={activeTab}>
        <Grid2
          container
          alignItems="center"
          spacing={1}
          sx={{ borderBottom: "1px solid lightgray", width: "100%" }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                label={
                  <Box>
                    {tab.label}
                    {tab.value !== "Patients" && tab.value !== "Dashboard" && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloseTab(tab.value);
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                }
                value={tab.value}
              />
            ))}
          </Tabs>
        </Grid2>

        <Grid2 item size={12}>
          {tabs.map((tab) => (
            <TabPanel
              key={tab.value}
              value={tab.value}
              keepMounted={tab.value !== "Patients"}
              sx={{ padding: "0px", width: "100%" }}
            >
              {tab.value === "Patients" && (
                <PatientList
                  ref={patientListRef}
                  onViewDetails={handleViewDetails}
                  onEditPatient={handleEditPatientTab}
                  onNewPatient={handleNewPatientTab}
                />
              )}
              {tab.value === "Dashboard" && (
  <PatientDashboard />
)}
              {tab.type === "PatientDetails" && (
                <PatientDetails
                  patientId={tab.patientId}
                  onOpenTab={handleEditPatientTab}
                  onViewDocument={openPrescriptionViewTab}
                />
              )}
              {tab.type === "ViewPrescription" && (
                <PrescriptionReadOnlyView
                  key={tab.prescriptionId}
                  prescriptionId={tab.prescriptionId}
                  prescriptionType={tab.prescriptionType}
                />
              )}
              {tab.type === "EditPatient" && (
                <EditPatient patient={tab.patient} onSave={handleSaveEdit}
                uniqueId={`EditPatient-${tab.patient?.id}`}
                 />
              )}
              {tab.type === "NewPatient" && (
                <NewPatient
                  tempNewPatientId={tab.tempNewPatientId}
                  onNewPatientAdded={onNewPatientAdded}
                      uniqueId={`NewPatient-${tab.tempNewPatientId}`} 

                />
              )}
              {tab.content && React.isValidElement(tab.content)
                ? tab.content
                : null}
            </TabPanel>
          ))}
        </Grid2>
      </TabContext>
    </Grid2>
    <UnsavedDataConfirmationDialog
  key={tabToClose || activeTab}
  open={showUnsavedChangesDialog}
  onClose={() => {
    setShowUnsavedChangesDialog(false);
    setTabToClose(null);
  }}
  onDiscardChanges={handleDiscardChanges}
  title="Unsaved Changes?"
  message="You have unsaved changes. Are you sure you want to discard them?"
  discardButtonText="Discard Changes"
  cancelButtonText="Keep Changes"
/>
    </>
  );
}

export default PatientHome;